import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { useTags, TagChips, KindBadge, Modal } from "../components/ui";
import { WhatsAppButton } from "../components/WhatsAppButton";
import QuickActions from "../components/QuickActions";
import Contact360 from "../components/Contact360";
import SentComms from "../components/SentComms";
import ClientFiles from "../components/ClientFiles";
import { ReviewRequestButton } from "../components/ReviewRequest";
import PersonTasks from "../components/PersonTasks";
import AcquistiContatto from "../components/AcquistiContatto";

export default function PersonDetail() {
  const { id } = useParams();
  const { tags } = useTags();
  const [data, setData] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [linkModal, setLinkModal] = useState(false);
  const [linkCompany, setLinkCompany] = useState("");
  const [linkRole, setLinkRole] = useState("");
  const navigate = useNavigate();
  const [convertModal, setConvertModal] = useState(false);
  const [convName, setConvName] = useState("");
  const [convRegime, setConvRegime] = useState("");
  const [convVat, setConvVat] = useState("");
  const [convRole, setConvRole] = useState("Titolare");
  const [converting, setConverting] = useState(false);

  const load = useCallback(() => {
    api.get(`/persons/${id}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/companies").then((r) => setAllCompanies(r.data)).catch(() => {}); }, []);

  if (!data) return <p className="muted">Caricamento…</p>;
  const { person, companies } = data;

  const addLink = async () => {
    if (!linkCompany) return;
    try {
      await api.post("/persons/links", { person_id: id, company_id: linkCompany, role: linkRole || null });
      setLinkModal(false); setLinkCompany(""); setLinkRole(""); load();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const removeLink = async (linkId) => { await api.delete(`/persons/links/${linkId}`); load(); };

  const openConvert = () => {
    setConvName(`${person.first_name || ""} ${person.last_name || ""}`.trim());
    setConvertModal(true);
  };
  const doConvert = async () => {
    if (!convName.trim()) return;
    setConverting(true);
    try {
      const r = await api.post(`/persons/${id}/convert-to-company`, {
        name: convName.trim(),
        regime: convRegime || null,
        vat_number: convVat.trim() || null,
        role: convRole.trim() || "Titolare",
      });
      navigate(`/aziende/${r.data.company_id}`);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setConverting(false); }
  };

  const toggleReview = async (val) => {
    try { await api.put(`/persons/${id}`, { review_given: val }); load(); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div>
      <p><Link to="/persone">← Persone</Link></p>
      <h1 className="page-title">{person.last_name} {person.first_name} <KindBadge kind={person.kind} /></h1>
      <p className="page-sub"><TagChips tagIds={person.tag_ids} tags={tags} /></p>

      <div className="toolbar" style={{ marginBottom: 8 }} data-testid="person-quick-actions">
        <QuickActions target={{ kind: "person", id: person.id, name: `${person.first_name || ""} ${person.last_name || ""}`.trim(), email: person.email, phone: person.phone, company: person.company }} />
        <div className="spacer" />
        <button className="btn sm" onClick={openConvert} data-testid="convert-to-company-btn" title="Crea un'azienda da questa persona (es. ditta individuale) e la collega come referente">🏢 Trasforma in azienda</button>
        <button className="btn sm danger" data-testid="person-delete-btn" onClick={async () => {
          if (!window.confirm(`Eliminare "${person.first_name || ""} ${person.last_name || ""}" e i suoi collegamenti? Le email ricevute non vengono toccate.`)) return;
          try { await api.delete(`/persons/${person.id}`); navigate("/contatti"); }
          catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
        }}>🗑 Elimina</button>
      </div>

      <Contact360 email={person.email} phone={person.phone} name={`${person.first_name || ""} ${person.last_name || ""}`.trim()} hide={["person"]} />
      <AcquistiContatto email={person.email} />

      <div className="panel">
        <h3>Dati</h3>
        <div className="grid-2">
          <div><strong>Ruolo:</strong> {person.role || "—"}</div>
          <div><strong>Cod. fiscale:</strong> {person.fiscal_code || "—"}</div>
          <div><strong>Email:</strong> {person.email || "—"}</div>
          <div><strong>Telefono:</strong> {person.phone || "—"} <WhatsAppButton phone={person.phone} name={`${person.first_name || ""} ${person.last_name || ""}`.trim()} /></div>
          {person.company && <div><strong>Azienda:</strong> {person.company}</div>}
        </div>
        {person.notes && <p style={{ marginTop: 12 }}><strong>Note:</strong> {person.notes}</p>}
      </div>

      <PersonTasks personId={id} />

      <ClientFiles personId={id} />
      <SentComms params={`person=${id}`} testid="person-sent-comms" />

      <div className="panel">
        <h3>Consenso privacy</h3>
        {person.privacy ? (
          <p>
            <span className="pill gestita">Consenso accettato</span>{" "}
            {person.privacy_date && <span className="muted">il {new Date(person.privacy_date).toLocaleDateString("it-IT")}</span>}
            {person.privacy_source && <span className="muted"> · origine: {person.privacy_source}</span>}
          </p>
        ) : (
          <p><span className="pill da_gestire">Consenso non registrato</span></p>
        )}
        <p className="muted">Consenso all'inserimento nel CRM e alla ricezione di email e materiali informativi.</p>
      </div>

      <div className="panel">
        <div className="toolbar" style={{ marginBottom: 4 }}>
          <h3 style={{ margin: 0 }}>Recensione Google</h3>
          <div className="spacer" />
          <ReviewRequestButton name={`${person.first_name || ""} ${person.last_name || ""}`.trim()} email={person.email} phone={person.phone} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="person-review-given-label">
          <input type="checkbox" checked={!!person.review_given} onChange={(e) => toggleReview(e.target.checked)} data-testid="person-review-given" />
          <span>Recensione Google già lasciata</span>
        </label>
        <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>Se spuntata, a questo contatto <strong>non</strong> verrà più inviata la richiesta di recensione dopo gli appuntamenti.{person.review_date ? ` (registrata il ${new Date(person.review_date).toLocaleDateString("it-IT")})` : ""}</p>
      </div>

      <div className="panel">
        <div className="toolbar"><h3 style={{ margin: 0 }}>Aziende collegate</h3><div className="spacer" /><button className="btn sm primary" onClick={() => setLinkModal(true)}>+ Collega azienda</button></div>
        <table>
          <thead><tr><th>Azienda</th><th>Ruolo</th><th></th></tr></thead>
          <tbody>
            {companies.map((cl) => (
              <tr key={cl.link_id}>
                <td><Link to={`/aziende/${cl.company.id}`}>{cl.company.name}</Link></td>
                <td>{cl.role || "—"}</td>
                <td><button className="btn sm danger" onClick={() => removeLink(cl.link_id)}>Scollega</button></td>
              </tr>
            ))}
            {companies.length === 0 && <tr><td colSpan={3} className="muted">Nessuna azienda collegata.</td></tr>}
          </tbody>
        </table>
      </div>

      {convertModal && (
        <Modal title="Trasforma in azienda" onClose={() => setConvertModal(false)}>
          <p className="muted" style={{ marginTop: 0 }}>Crea una nuova azienda cliente con i dati di questa persona. La persona resterà come referente collegato e continuerà a ricevere email e comunicazioni. L'azienda comparirà automaticamente in Contabilità.</p>
          <div className="field"><label>Ragione sociale</label><input value={convName} onChange={(e) => setConvName(e.target.value)} data-testid="convert-name-input" /></div>
          <div className="field"><label>Regime contabile (opzionale)</label>
            <select value={convRegime} onChange={(e) => setConvRegime(e.target.value)} data-testid="convert-regime-select">
              <option value="">— non impostato —</option>
              <option value="forfettario">Forfettario</option>
              <option value="semplificato">Semplificato</option>
              <option value="ordinario">Ordinario</option>
            </select>
          </div>
          <div className="field"><label>Partita IVA (opzionale)</label><input value={convVat} onChange={(e) => setConvVat(e.target.value)} data-testid="convert-vat-input" /></div>
          <div className="field"><label>Ruolo della persona nell'azienda</label><input value={convRole} onChange={(e) => setConvRole(e.target.value)} data-testid="convert-role-input" /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setConvertModal(false)}>Annulla</button>
            <button className="btn primary" onClick={doConvert} disabled={!convName.trim() || converting} data-testid="convert-confirm-btn">{converting ? "Creazione…" : "Crea azienda"}</button>
          </div>
        </Modal>
      )}

      {linkModal && (
        <Modal title="Collega un'azienda" onClose={() => setLinkModal(false)}>
          <div className="field"><label>Azienda</label>
            <select value={linkCompany} onChange={(e) => setLinkCompany(e.target.value)}>
              <option value="">— seleziona —</option>
              {allCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Ruolo nel collegamento</label><input placeholder="es. Consulente paghe" value={linkRole} onChange={(e) => setLinkRole(e.target.value)} /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setLinkModal(false)}>Annulla</button>
            <button className="btn primary" onClick={addLink} disabled={!linkCompany}>Collega</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
